import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Table } from "react-bootstrap";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ROLE_EDIT, ROLE_ADD } from "../../../../services/ApiCalls";
import ButtonLoader from "../../../../components/Common/ButtonLoader";
const RoleForm = ({ data }) => {
  const { t } = useTranslation();
  const [loader, setLoader] = useState(false);
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      _id: data?._id || "",
      roleName: data?.roleName || "",
      status: data?.status === "Active" || data?.status === true,
      permission: {},
      country: data?.country || [],
      countryAccess: data?.countryAccess || ["thailand", "vietnam"],
    },
  });

  // Watch country access to show/hide description
  const countryAccess = watch("countryAccess") || [];

  const defaultPermissions = [
    { module: "Dashboard", label: t("roleManagement.form.modules.dashboard") },
    { module: "ManageUser", label: t("roleManagement.form.modules.manageUser") },
    { module: "ManageHotel", label: t("roleManagement.form.modules.manageHotel") },
    { module: "ManageCoupon", label: t("roleManagement.form.modules.manageCoupon") },
    { module: "ManageBooking", label: t("roleManagement.form.modules.manageBooking") },
    { module: "ManagePromotion", label: t("roleManagement.form.modules.managePromotion") },
    { module: "AppIntro", label: t("roleManagement.form.modules.appIntro") },
    { module: "Finance", label: t("roleManagement.form.modules.finance") },
    { module: "ManageSupportCenter", label: t("roleManagement.form.modules.manageSupportCenter") },
    { module: "SystemAccess", label: t("roleManagement.form.modules.systemAccess") },
    { module: "Logs", label: t("roleManagement.form.modules.logs") },
    { module: "Settings", label: t("roleManagement.form.modules.settings") },
  ];

  useEffect(() => {
    if (data?.permission) {
      const permissionObj = data.permission;
      Object.keys(permissionObj).forEach((modKey) => {
        const moduleData = permissionObj[modKey];
        if (moduleData.sections) {
          Object.keys(moduleData.sections).forEach((sectionKey) => {
            const perms = moduleData.sections[sectionKey];
            setValue(
              `permission.${modKey}.sections.${sectionKey}.view`,
              perms.view
            );
            setValue(
              `permission.${modKey}.sections.${sectionKey}.add_edit`,
              perms.add_edit
            );
            setValue(
              `permission.${modKey}.sections.${sectionKey}.delete`,
              perms.delete
            );
          });
        } else {
          setValue(`permission.${modKey}.view`, moduleData.view);
          setValue(`permission.${modKey}.add_edit`, moduleData.add_edit);
          setValue(`permission.${modKey}.delete`, moduleData.delete);
        }
      });
    }

    // Set country access from data
    if (data?.countryAccess) {
      setValue("countryAccess", data.countryAccess);
    }
  }, [data, setValue]);

  const onSubmit = async (formData) => {
    try {
      setLoader(true);

      const payload = {
        ...formData,
        status: formData.status ? "Active" : "Inactive",
      };

      let response;
      if (data?._id) {
        response = await ROLE_EDIT(payload);
      } else {
        delete payload._id;
        response = await ROLE_ADD(payload);
      }
      if (response?.data?.status === "success") {
        toast.success(response?.data?.message || t("roleManagement.form.messages.submitSuccess"));
        navigate("/role");
      } else {
        toast.error(response?.data?.message);
      }
    } catch (error) {
      toast.error(error?.message || t("roleManagement.form.messages.submitFailed"));
    } finally {
      setLoader(false);
    }
  };

  const renderToggle = (id, path) => (
    <div className="form-check form-switch permisionSwitch">
      <input
        type="checkbox"
        className="form-check-input"
        id={id}
        {...register(path)}
      />
    </div>
  );

  const handleCountryToggle = (country) => {
    const currentCountries = watch("countryAccess") || [];
    let newCountries;

    if (currentCountries.includes(country)) {
      newCountries = currentCountries.filter((c) => c !== country);
    } else {
      newCountries = [...currentCountries, country];
    }
    setValue("countryAccess", newCountries);
  };

  return (
    <Col lg="12" className="my-2">
      <div className="cardCstm p-3 rounded bg-white py-5">
        <Form
          className="mx-auto"
          style={{ maxWidth: 777 }}
          onSubmit={handleSubmit(onSubmit)}
        >
          <Row>
            <Col md="6" className="my-2 my-lg-4">
              <label className="form-label text-muted m-0 pb-1 fw-sbold">
                {t("roleManagement.form.role")}
              </label>
              <input
                type="text"
                className="form-control"
                placeholder={t("roleManagement.form.rolePlaceholder")}
                {...register("roleName", { required: t("roleManagement.form.validation.roleRequired") })}
              />
              {errors?.roleName && (
                <p className="text-danger m-0">{errors.roleName.message}</p>
              )}
            </Col>

            <Col lg="6" className="my-2 permisionSwitch ">
              <Form.Group controlId="status">
                <Form.Label className="form-label text-muted m-0 pb-1 fw-sbold mt-3">
                  {t("roleManagement.form.status")}
                </Form.Label>

                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <Form.Check
                      type="switch"
                      id="active-toggle"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  )}
                />
              </Form.Group>
            </Col>

            {/* Country Access Section */}
            <Col lg="12" className="my-3">
              <Form.Group controlId="countryAccess">
                <div className="d-flex justify-content-between">
                  <div className="d-flex flex-column gap-3">
                    <Form.Label className="form-label text-muted m-0 pb-2 fw-sbold">
                      {t("roleManagement.form.countryAccess")}
                    </Form.Label>
                    {/* <div className="mt-2">
                      <small className="text-muted">
                        Access only to hotels, transactions, and data under the
                        assigned country.
                      </small>
                    </div> */}
                  </div>

                  <div className="d-flex gap-4 mb-2">
                    <Form.Check
                      type="checkbox"
                      id="thailand-checkbox"
                      label={
                        <span className="d-flex align-items-center">
                          <span className="me-2">📍</span>
                          {t("roleManagement.form.thailand")}
                        </span>
                      }
                      checked={countryAccess.includes("thailand")}
                      onChange={() => handleCountryToggle("thailand")}
                      className="fw-medium"
                    />

                    <Form.Check
                      type="checkbox"
                      id="vietnam-checkbox"
                      label={
                        <span className="d-flex align-items-center">
                          <span className="me-2">📍</span>
                          {t("roleManagement.form.vietnam")}
                        </span>
                      }
                      checked={countryAccess.includes("vietnam")}
                      onChange={() => handleCountryToggle("vietnam")}
                      className="fw-medium"
                    />
                  </div>
                </div>
              </Form.Group>
            </Col>

            <div className="card shadow-sm rounded-3 p-3 border-0 bg-light mt-4">
              <h5 className="fw-bold mb-3 d-flex align-items-center">
                <i className="bi bi-shield-lock-fill me-2 text-primary"></i>
                {t("roleManagement.form.rolePermissions")}
                <span
                  className="ms-2"
                  data-bs-toggle="tooltip"
                  data-bs-placement="right"
                  title={t("roleManagement.form.permissionsTooltip")}
                >
                  <i className="bi bi-info-circle text-muted"></i>
                </span>
              </h5>

              <Table responsive bordered hover className="mb-0 text-center">
                <thead className="table-light">
                  <tr>
                    <th className="text-start">{t("roleManagement.form.moduleSection")}</th>
                    <th>{t("roleManagement.form.view")}</th>
                    <th>{t("roleManagement.form.addEdit")}</th>
                    <th>{t("roleManagement.form.delete")}</th>
                  </tr>
                </thead>
                <tbody>
                  {defaultPermissions.map((mod) =>
                    mod.sections ? (
                      mod.sections.map((section) => (
                        <tr key={`${mod.module}-${section}`}>
                          <td className="text-start ps-3">
                            {mod.module} - {section}
                          </td>
                          {["view", "add_edit", "delete"].map((perm) => (
                            <td key={perm}>
                              {renderToggle(
                                `${mod.module}-${section}-${perm}`,
                                `permission.${mod.module}.sections.${section}.${perm}`
                              )}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr key={mod.module}>
                        <td className="text-start ps-3">{mod.label}</td>
                        {["view", "add_edit", "delete"].map((perm) => (
                          <td key={perm}>
                            {renderToggle(
                              `${mod.module}-${perm}`,
                              `permission.${mod.module}.${perm}`
                            )}
                          </td>
                        ))}
                      </tr>
                    )
                  )}
                </tbody>
              </Table>
            </div>

            <Col lg="12" className="my-4 text-center">
              <Button
                type="submit"
                className="px-4 rounded-3"
                disabled={loader}
              >
                {loader ? <ButtonLoader /> : t("roleManagement.form.submit")}
              </Button>
            </Col>
          </Row>
        </Form>
      </div>
    </Col>
  );
};

export default RoleForm;
